/**
 * Wrapper around XMLHttpRequest, suitable for x-browser compatibility
 * TODO: Refactor module functions and name to be generic requests
 * @module
 */
var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var DateEnv = require('core/env/date');
var Flux = require('../flux');
var JSONEnv = require('core/lib/json');
var Logger = require('./logger');
var PromiseEnv = require('../lib/promise').Promise;
var Sandbox = require('./sandbox_natives');

/**
 * @constant
 * @type {number}
 * @private
 */
var XHR_MAX_RETRIES = 3;

/**
 * @typedef {Object} XhrOptions
 * @property {string} url - The URL to open
 * @property {string} [method] - HTTP method to use (default "GET")
 * @property {string} [contentType] - 'Content-Type' header to use (default: 'text/plain;charset=UTF-8')
 * @property {boolean} [async] - Whether to use async request (default true)
 * @property {boolean} [withCredentials] - If set, passed through to XMLHttpRequest.withCredentials.
 *    See https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/withCredentials for details.
 * @property {string|Object} [data] - Data string to send with request (JSON format). If
 *    an object, that object is JSONEnv.stringify'd.
 * @property {function} [success] - Success callback (for sync usage)
 * @property {function} [error] - Error callback (for sync usage)
 */

/**
 * Is XHR CORS supported in this browser?
 * @returns {boolean}
 */
exports.isCORSSupported = function(): boolean {
  var sandboxedXML = Sandbox.get('XMLHttpRequest');
  return 'withCredentials' in new sandboxedXML();
};

/**
 * Make a (cross-domain) XHR request.
 *
 * @param {module:utils/xhr~XhrOptions} options
 * @returns {Promise} Promise that resolves when the XHR loads and rejects if it errors
 */
exports.request = function(options:import('core/utils/xhr').XhrOptions): Promise<any> {
  options = _.extend({
    method: 'GET',
    async: true,
    contentType: 'text/plain;charset=UTF-8',
  }, options);

  return new PromiseEnv(function(resolve, reject) {
    // TODO(tyler): XDomain for x-domain XHR on IE<10
    // For now assume all XHR are CORS
    if (!exports.isCORSSupported()) {
      return reject('CORS is not supported');
    }

    var sandboxedXML = Sandbox.get('XMLHttpRequest');
    var xhr = new sandboxedXML();

    // Attach listeners for success/failure
    xhr.onload = function() {
      if (options.success) {
        options.success(xhr);
      }
      resolve(xhr);
    };

    xhr.onerror = function() {
      if (options.error) {
        options.error(xhr);
      }
      reject(xhr);
    };

    if (_.isObject(options.data)) {
      options.data = JSONEnv.stringify(options.data);
    }
    xhr.open(options.method, options.url, options.async);

    if (options.withCredentials) {
      xhr.withCredentials = options.withCredentials;
    }

    xhr.setRequestHeader('Content-Type', options.contentType);

    if(_.isObject(options.headers)) {
      _.forEach(options.headers, function(value, key){
        xhr.setRequestHeader(key, value);
      });
    }

    xhr.send(options.data);
  });
};

/**
 * Make a (cross-domain) AJAX request that retries up to 3 times on failure.
 * Modifies data by adding a retryCount
 *
 * @param {module:utils/xhr~XhrOptions} options
 * @param {string} uniqueId (triggers guaranteed delivery, using local storage and stores/pending_events)
 * @param {number} [retryCount] Number of times we've tried to resend the event
 * @param {number} [maxRetries] Number of times to retry the event
 * @returns {Promise} Promise that resolves when the XHR loads and rejects if it errors
 */
// TODO (Lauren): Change params to an object so caller can specify maxRetries without retryCount (CJS-1065)
exports.retryableRequest = function(options:import('core/utils/xhr').XhrOptions, uniqueId:string, retryCount?:number, maxRetries?:number): Promise<any> {
  if (!uniqueId) {
    return PromiseEnv.reject(new Error('No id specified for request.'));
  }
  // Reject it early before trying xhr
  if (!exports.isCORSSupported()) {
    return PromiseEnv.reject(new Error('CORS is not supported.'));
  }

  if (_.isUndefined(maxRetries)) {
    maxRetries = XHR_MAX_RETRIES;
  }
  if (_.isUndefined(retryCount)) {
    retryCount = 0;
  }

  var pendingEvent = {
    id: uniqueId,
    timeStamp: DateEnv.now(),
    data: options,
    retryCount: retryCount,
  };

  Flux.dispatch(actionTypes.SET_PENDING_EVENT, pendingEvent);
  Logger.debug('Sending event ', uniqueId);

  return exports.request(options).then(function(xhrObj) {
    // Remove from the store if event was successfully sent
    Flux.dispatch(actionTypes.REMOVE_PENDING_EVENT, {
      id: uniqueId,
    });
    return xhrObj;
  }, function(errObj) {
    if (pendingEvent.retryCount >= maxRetries) {
      // Only remove from the store on failure if we've tried three times.
      Flux.dispatch(actionTypes.REMOVE_PENDING_EVENT, {
        id: uniqueId,
      });
      Logger.warn('Event ', pendingEvent, ' could not be sent after ', maxRetries, ' attempts.');
    } else {
      // Otherwise update the retry count on the event in the store
      pendingEvent.retryCount++;
      Flux.dispatch(actionTypes.SET_PENDING_EVENT, pendingEvent);
      Logger.debug('Event ', pendingEvent, ' failed to send, with error ', errObj,
                   ' It will be retried ', (maxRetries - retryCount), ' times.');
    }
    throw errObj;
  });
};

/**
 * Send data via sendBeacon or fall back to XHR POST request if sendBeacon unavailable or fails.
 * Enabled if __COMPAT__.SENDBEACON is set true
 *
 * @param {Object} options
 * @param {string} options.url The endpoint URL
 * @param {string} options.contentType 'Content-Type' of the data to send
 * @param {string|*} options.data Data string to send (Will JSONEnv.stringify if not string)
 * @param {string} [uniqueId] (triggers guaranteed delivery, using local storage and stores/pending_events if sendBeacon is not supported)
 * @returns {Promise} Promise that resolves if data can be sent using sendBeacon or not
 */
exports.sendBeacon = __COMPAT__.SENDBEACON ? function (options, uniqueId) {
  if (!options.url || !options.data ) {
    return PromiseEnv.reject(new Error('Must provide required options'));
  }
  if (navigator && navigator.sendBeacon) {
    try {
      var data = _.isObject(options.data) ?
        JSONEnv.stringify(options.data) : options.data;
      if (navigator.sendBeacon(options.url, data)) {
        return PromiseEnv.resolve();
      } else {
        throw new Error('Unable to sendBeacon');
      }
    } catch(e) {
      Logger.warn('sendBeacon failed: ', e);
    }
  }

  //if sendBeacon throws, fails or does not exist
  options = _.extend({
    method: 'POST',
  }, options);
  return exports.request(options, uniqueId);
} : exports.request;
